import { useRef } from 'react';
import { Box, Grid } from '@mui/material';
import router from 'next/router';
import { NoSearchResults } from '@/components/feature/searchDirectory/NoSearchResults';
import { SearchResults } from '@/components/feature/searchDirectory/SearchResults';
import { useSearchDirectoryData } from '@/context/searchDirectory/SearchDirectoryDataContext';
import { usePageAttributes } from '@/components/contexts/PageAttributesContext';
import { seoSegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_jobSearchResults as JobSearchResult } from '@/__generated__/seoSegmentationListingPage';
import OffsetPagination from '@/components/SearchResults/Pagination';
import { useSearchResults } from '@/components/feature/searchDirectory/useSearchResults';
import { SEOSegmentationPageType } from '@/__generated__/globalTypes';
import Filters from '@/components/Filters/LegacyFilters/Filters';
import { ItemListSchema } from '@/components/feature/searchDirectory/ItemListSchema';
import { JobCard } from './JobCard/JobCard';

export interface SearchResultsContainerProps {
  Aside: React.ComponentType;
}

export const SearchResultsContainer = ({ Aside }: SearchResultsContainerProps) => {
  const { seoSegmentationSearchResults, seoSegmentationDetails } = useSearchDirectoryData();
  const { enrollmentURL, seoSegmentId } = usePageAttributes();
  const {
    jobSearchResults: initialJobSearchResults,
    filters: initialFilters,
    pagination: initialPagination,
  } = seoSegmentationSearchResults;

  const isNationalPage = seoSegmentationDetails.pageType === SEOSegmentationPageType.NATIONAL;
  const searchFiltersRef = useRef<HTMLDivElement>(null);
  const searchResultRef = useRef<HTMLInputElement | null>(null);

  const validFilters =
    seoSegmentationDetails.searchFilters?.filter(
      (item): item is NonNullable<typeof item> => item !== null
    ) ?? [];

  const {
    pagination,
    jobSearchResults,
    pageNumber,
    handlePageClick: baseHandlePageClick,
  } = useSearchResults({
    initialFilters,
    initialPagination,
    seoSegmentId,
    isNationalPage,
    showUserFilters: false,
    initialSearchResults: {
      jobSearchResults: initialJobSearchResults,
    },
  });

  const handlePageClick = (newPage: number) => {
    baseHandlePageClick(newPage, searchResultRef);
  };
  const handleFiltersApply = () => {
    handlePageClick(1); // pull Apply changes from localStorage but set page as 1
    router.push(seoSegmentId, undefined, { shallow: true });
  };

  return (
    <>
      <Grid item xs={12}>
        <Filters
          searchFilters={validFilters}
          searchResultsHeader=""
          onChange={handleFiltersApply}
          searchFiltersRef={searchFiltersRef}
          enrollmentURL={enrollmentURL ?? ''}
          serviceId={seoSegmentationDetails.serviceId}
          serviceIdForMember={seoSegmentationDetails.serviceIdForMember}
          showMoreFilters={false}
          showJobDetails={jobSearchResults.length > 0}
          searchingFor={seoSegmentationDetails.searchingFor}
        />
      </Grid>
      <Grid container>
        <Grid item xs={12} md={8}>
          {jobSearchResults.length === 0 ? (
            <Box mt={4} mb={6}>
              <NoSearchResults />
            </Box>
          ) : (
            <Box ref={searchResultRef}>
              <SearchResults<JobSearchResult>
                items={jobSearchResults}
                renderItem={(item, i) => <JobCard searchResult={item} key={`product-${i}`} />}
              />

              <ItemListSchema
                searchResultType="JOB"
                jobSearchResults={jobSearchResults}
                totalNumberOfResults={pagination.totalNumberOfResults}
                startIndex={pagination.startIndex}
              />

              <OffsetPagination
                onPageClick={handlePageClick}
                pagination={pagination}
                segmentId={seoSegmentId}
                pageSelected={pageNumber}
              />
            </Box>
          )}
        </Grid>
        {/* 
              Offset md 1 grid column 
              Grid v2 adds offset prop, but we are still on grid v1
              https://v5.mui.com/material-ui/react-grid2/#offset
              */}
        <Grid item xs={false} md={1} />
        <Grid bgcolor={{ xs: 'background.default', sm: 'transparent' }} item xs={12} md={3}>
          <Aside />
        </Grid>
      </Grid>
    </>
  );
};
